import { jwtDecode } from "jwt-decode";
import { AuthRequestError } from "./fetchers";
import type {
  AuthorizeOptions,
  AuthSnapshot,
  AuthStorage,
  DecodeClaims,
  JwtClaims,
  OAuthConfig,
  OAuthFetchers,
  TokenResponse,
  TransactionStore,
  User,
} from "./types";

export interface AuthStoreDeps {
  config: OAuthConfig;
  fetchers: OAuthFetchers;
  storage: AuthStorage;
  transaction: TransactionStore;
  decode?: DecodeClaims;
  skewMs?: number;
  redirect?: (url: string) => void;
  reload?: () => void;
  randomState?: () => string;
  isOnline?: () => boolean;
}

// Only these mean the refresh token is actually rejected; anything else
// (network failure, offline, timeout, 5xx) is transient and must NOT log out.
function isAuthRejection(error: unknown): boolean {
  return (
    error instanceof AuthRequestError &&
    (error.status === 400 || error.status === 401)
  );
}

const UNAUTHENTICATED: AuthSnapshot = {
  status: "unauthenticated",
  user: null,
  claims: null,
  accessToken: null,
  tokens: null,
};

function defaultRandomState(): string {
  const bytes = new Uint32Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (n) => n.toString(16).padStart(8, "0")).join("");
}

export class AuthStore {
  private readonly config: OAuthConfig;
  private readonly fetchers: OAuthFetchers;
  private readonly storage: AuthStorage;
  private readonly transaction: TransactionStore;
  private readonly decode: DecodeClaims;
  private readonly skewMs: number;
  private readonly redirect: (url: string) => void;
  private readonly reload: () => void;
  private readonly randomState: () => string;
  private readonly isOnline: () => boolean;

  private snapshot: AuthSnapshot = UNAUTHENTICATED;
  private readonly subscribers = new Set<() => void>();

  private refreshPromise: Promise<string> | null = null;
  private initPromise: Promise<void> | null = null;
  private unsubscribeStorage: (() => void) | null = null;

  constructor(deps: AuthStoreDeps) {
    this.config = deps.config;
    this.fetchers = deps.fetchers;
    this.storage = deps.storage;
    this.transaction = deps.transaction;
    this.decode = deps.decode ?? ((token) => jwtDecode<JwtClaims>(token));
    this.skewMs = deps.skewMs ?? 30_000;
    this.redirect = deps.redirect ?? ((url) => window.location.assign(url));
    this.reload = deps.reload ?? (() => window.location.reload());
    this.randomState = deps.randomState ?? defaultRandomState;
    this.isOnline = deps.isOnline ?? (() => navigator.onLine);
  }

  subscribe = (callback: () => void): (() => void) => {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  };

  getSnapshot = (): AuthSnapshot => this.snapshot;

  init(): Promise<void> {
    this.initPromise ??= this.doInit();
    return this.initPromise;
  }

  private async doInit(): Promise<void> {
    this.unsubscribeStorage ??= this.storage.onExternalClear(() => {
      this.clearAuth();
      this.reload();
    });

    if (this.snapshot.status === "authenticated") return;

    // Restore the session from stored tokens without a network call. The access
    // token is valid for ~1h; a stale one is refreshed lazily by getToken() or
    // the 401 interceptor, not on every page load.
    const stored = this.storage.getTokens();
    if (!stored?.access_token) {
      this.setState(UNAUTHENTICATED);
      return;
    }
    try {
      this.applyTokens(stored);
    } catch {
      this.clearAuth();
    }
  }

  beginLogin(options: AuthorizeOptions = {}): void {
    const state = this.randomState();
    this.transaction.setState(state);
    if (options.returnTo) {
      this.transaction.setReturnTo(options.returnTo);
    } else {
      this.transaction.clearReturnTo();
    }

    const params = new URLSearchParams({
      response_type: "code",
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      scope: this.config.scope,
      state,
      audience: this.config.audience,
    });
    if (options.country) params.set("country", options.country);
    if (options.loginMethod) params.set("login_method", options.loginMethod);
    if (options.language) params.set("language", options.language);
    if (options.prompt) params.set("prompt", options.prompt);

    this.redirect(`${this.config.authUrl}?${params.toString()}`);
  }

  async completeLogin(search: URLSearchParams): Promise<void> {
    const returnedState = search.get("state");
    const storedState = this.transaction.getState();
    this.transaction.clear();

    const errorParam = search.get("error");
    if (errorParam) {
      this.setState(UNAUTHENTICATED);
      throw new Error(search.get("error_description") ?? errorParam);
    }

    if (!returnedState || returnedState !== storedState) {
      this.setState(UNAUTHENTICATED);
      throw new Error("invalid state parameter");
    }

    const code = search.get("code");
    if (!code) {
      this.setState(UNAUTHENTICATED);
      throw new Error("missing authorization code");
    }

    const tokens = await this.fetchers.exchangeCode({
      tokenUrl: this.config.tokenUrl,
      code,
      redirectUri: this.config.redirectUri,
      clientId: this.config.clientId,
    });
    this.applyTokens(tokens);
  }

  // Read and clear the post-login return target captured at beginLogin. Caller
  // must validate it is a local path before navigating (open-redirect guard).
  consumeReturnTo(): string | null {
    const url = this.transaction.getReturnTo();
    this.transaction.clearReturnTo();
    return url;
  }

  logout(): void {
    const idToken = this.storage.getTokens()?.id_token ?? null;
    const params = new URLSearchParams({
      post_logout_redirect_uri: this.config.postLogoutRedirectUri,
      state: this.randomState(),
    });
    if (idToken) params.set("id_token_hint", idToken);
    this.redirect(`${this.config.logoutUrl}?${params.toString()}`);
  }

  completeLogout(): void {
    this.clearAuth();
  }

  getToken(): Promise<string> {
    const { accessToken, claims } = this.snapshot;
    if (accessToken && claims && !this.isExpiring(claims)) {
      return Promise.resolve(accessToken);
    }
    return this.refresh();
  }

  refresh(): Promise<string> {
    // Never hit the network while offline: a failed refresh must not log the
    // user out. Reject without touching auth state; the session is preserved.
    if (!this.isOnline()) {
      return Promise.reject(new Error("offline"));
    }
    this.refreshPromise ??= this.doRefresh().finally(() => {
      this.refreshPromise = null;
    });
    return this.refreshPromise;
  }

  private async doRefresh(): Promise<string> {
    const refreshToken = this.storage.getTokens()?.refresh_token;
    if (!refreshToken) {
      this.clearAuth();
      throw new Error("no refresh token");
    }
    try {
      const tokens = await this.fetchers.refresh({
        tokenUrl: this.config.tokenUrl,
        refreshToken,
        clientId: this.config.clientId,
      });
      return this.applyTokens(tokens);
    } catch (error) {
      // Only a rejected refresh token logs out; transient failures keep session.
      if (isAuthRejection(error)) this.clearAuth();
      throw error;
    }
  }

  private applyTokens(tokens: TokenResponse): string {
    // Refresh responses may omit refresh_token/id_token (no rotation) — keep the
    // previously stored values so the raw object stays complete.
    const previous = this.storage.getTokens();
    const merged: TokenResponse = {
      ...tokens,
      refresh_token: tokens.refresh_token ?? previous?.refresh_token,
      id_token: tokens.id_token ?? previous?.id_token,
    };

    const accessClaims = this.decode(merged.access_token);
    const identityClaims = merged.id_token
      ? this.decode(merged.id_token)
      : accessClaims;

    this.storage.setTokens(merged);

    const user: User = {
      id: identityClaims.sub,
      email: identityClaims.email ?? "",
      name: identityClaims.name ?? "",
    };
    this.setState({
      status: "authenticated",
      user,
      claims: accessClaims,
      accessToken: merged.access_token,
      tokens: merged,
    });
    return merged.access_token;
  }

  private clearAuth(): void {
    this.storage.clear();
    this.setState(UNAUTHENTICATED);
  }

  private isExpiring(claims: JwtClaims): boolean {
    return claims.exp * 1000 <= Date.now() + this.skewMs;
  }

  private setState(next: AuthSnapshot): void {
    this.snapshot = next;
    for (const callback of this.subscribers) callback();
  }
}
